import { useCallback, useState } from "react";
import { useMsal } from "@azure/msal-react";
import type { AccountInfo } from "@azure/msal-browser";
import { MsalService } from "@/components/security/msal-service";
import { config } from "@/config/environment";

export function useMsalToken() {
  const { instance } = useMsal();
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  // Get centralized MSAL service
  const msalService = MsalService.getInstance(instance);

  const getAccessToken = useCallback(
    async (options?: { forceRefresh?: boolean }): Promise<string | null> => {
      const account = msalService.getActiveAccount();
      if (!account) {
        if (config.debug.enabled) {
          console.warn("No active account found");
        }
        return null;
      }

      return await msalService.acquireTokenSilent(account, options);
    },
    [msalService]
  );

  /**
   * Ensures that the account has a valid access token by refreshing if needed
   */
  const ensureValidToken = useCallback(
    async (account: AccountInfo): Promise<boolean> => {
      if (isRefreshingToken) {
        if (config.debug.enabled) {
          console.log("🔄 Token refresh already in progress, skipping");
        }
        return false;
      }

      setIsRefreshingToken(true);
      try {
        const success = await msalService.refreshToken(account);
        return success;
      } finally {
        setIsRefreshingToken(false);
      }
    },
    [msalService, isRefreshingToken]
  );

  const addAuthHeader = useCallback(
    async (headers: Headers): Promise<Headers> => {
      const token = await getAccessToken();
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
    [getAccessToken]
  );

  // Note: Token refresh is handled by useAuth hook to avoid loops
  // This hook only provides token utilities, not auto-refresh logic

  return {
    getAccessToken,
    addAuthHeader,
    ensureValidToken,
    isRefreshingToken,
    isAuthenticated: msalService.getActiveAccount() !== null
  };
}
